import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { CoreIntegrityRow } from "./use-integrity.ts";
import {
  coreRowStatus,
  coreStateLabel,
  coreFailureReasonLabel,
  coreEventsHref,
} from "./vocab.ts";

/**
 * Protected_Core state per instance (task 11.9; design 12.2 "Protected_Core state per instance";
 * 12.4 "Protected_Core state as a semantic table", status by icon+text).
 *
 * Reads the `/integrity/core` rows (service, `state`, `failure_reason`, `reported_at`, `stale`) and
 * renders them in the shared semantic `DenseTable`. Each row's state is a `StatusBadge` (icon+text,
 * never colour alone): only an explicitly `verified` core reads healthy; `degraded` is a warn;
 * `failed`/`absent` are errors; `other` is grey. A `stale` reading downgrades a would-be-healthy
 * state to grey — a silent instance is NEVER shown as green (design 12.3). The failure reason is
 * shown when present, and the instance drills to its integrity events.
 */

export interface CoreIntegrityTableProps {
  readonly rows: readonly CoreIntegrityRow[];
}

export function CoreIntegrityTable(props: CoreIntegrityTableProps): ReactNode {
  const navigate = useNavigate();

  const columns: readonly Column<CoreIntegrityRow>[] = [
    {
      key: "instance",
      header: "Instance",
      isRowHeader: true,
      cell: (row) => (
        <button
          type="button"
          className="itg-link"
          onClick={() => navigate(coreEventsHref(row))}
          aria-label={`Open the Protected_Core integrity events for instance ${row.instance_id}`}
        >
          {row.instance_id}
        </button>
      ),
    },
    {
      key: "service",
      header: "Service",
      cell: (row) => <span className="itg-faint">{row.service}</span>,
    },
    {
      key: "state",
      header: "State",
      cell: (row) => {
        const level = coreRowStatus(row);
        const label = row.stale ? `${coreStateLabel(row.state)} (stale)` : coreStateLabel(row.state);
        const title = row.stale
          ? `Protected_Core reported ${coreStateLabel(
              row.state,
            ).toLowerCase()}, but the reading is stale, so it is not shown as healthy.`
          : `Protected_Core state: ${coreStateLabel(row.state).toLowerCase()}.`;
        return <StatusBadge level={level} label={label} title={title} size="sm" />;
      },
    },
    {
      key: "reason",
      header: "Failure reason",
      cell: (row) =>
        row.failure_reason === null ? (
          <span className="itg-faint">n/a</span>
        ) : (
          <span className="itg-reason">{coreFailureReasonLabel(row.failure_reason)}</span>
        ),
    },
    {
      key: "reported",
      header: "Reported",
      align: "right",
      cell: (row) => (
        <time
          className="itg-num"
          dateTime={isoAttr(row.reported_at)}
          title={isoAttr(row.reported_at)}
        >
          {formatDateTime(row.reported_at)}
        </time>
      ),
    },
  ];

  return (
    <section className="itg-section" aria-label="Protected_Core state per instance">
      <div className="itg-section__head">
        <h2 className="itg-section__title">Protected_Core state</h2>
      </div>
      <DenseTable
        caption="Protected_Core attestation state per instance"
        columns={columns}
        rows={props.rows}
        rowKey={(row) => `${row.source_id}:${row.instance_id}`}
        stickyHeader
        emptyMessage="No Protected_Core state has been reported by any instance. Check that services are reporting their Protected_Core attestation."
      />
    </section>
  );
}
